import React from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Body5, Body7, Container } from '@ui';
import { Select } from '@ui/inputs';
import { CopyButton, FlatButton, SkeletonButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { getSpec } from '@services/scoreSpecs';
import { Consequence, MicroLabel, ReadOnlyJson } from '@features/scoreSpec/styles';
import { formatStatus } from '@features/scoreSpec/format';
import { SegmentedControl, statusSegments } from '@features/scoreSpec/SegmentedControl';
import { useScoreSpecs } from '@features/scoreSpec/useScoreSpecs';
import { writeTarget } from '@features/scoreSpec/environment';

// The read half of the paste mode: an id in, the response out, nothing in between.
//
// Same bargain as /create-score-spec. That one's contract is "what you typed is what I sent"; this
// one's is "what came back is what you see" — the whole record, formatted but not filtered, including
// the operational keys the spec definition itself never carries (`status`, `live_enabled`,
// `created_by`, the timestamps). Those are the reason a raw GET is worth having a screen for: the
// advanced editor shows the definition and infers the rest, and sometimes you want the row.
//
// The id is picked from a list rather than typed, the same control the edit mode uses — but over
// EVERY status by default, not just approved. This is an inspection tool: a draft somebody just
// created is the most likely thing to want to look at, and hiding it behind a filter meant for
// choosing what to copy would be the wrong borrowing. The status rides in each option's label so what
// is being picked is never ambiguous, and the switch above narrows the list when it gets long.
//
// A 404 can still happen and is still the server's answer, shown as it arrives — the store is
// per-environment, and a spec can be deleted between the list arriving and the fetch going out.
//
// The actions under the record are the two ways forward from a read: the advanced editor for this
// exact spec, and the edit mode for authoring a successor to an approved one. That second one lived
// on the specs list until it turned out to belong here, after the record it is a reaction to.
//
// `initialId` is the deep link: the advanced list opens a clicked row at /get-score-spec/:specId, and
// arriving with an id already named is a request for that record, so it fetches on mount instead of
// waiting for a Fetch click that would only re-state what the URL said. It fetches ONCE — a failed
// auto-fetch leaves its error up and hands the retry to the button, rather than re-firing on render.

const EnvBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $production }) => ($production ? theme.themeColors.warning : theme.themeColors.lowContrastBorder)};
`;

// `flex-start`, not `flex-end`: a labelled Select is label + input + hint, so aligning the row to its
// BOTTOM puts the button level with the hint text and floating below the control it acts on. The
// button is nudged down by the label's height instead — the same 22px offset OutPanel uses to line a
// row action up with its first labelled field.
const PickerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
`;

const FieldWrap = styled.div`
  flex: 1 1 320px;
`;

// FlatButton comes out 40px — 14px of label, 12px of padding either side, plus its 1px border — and
// the Select beside it is 38px. Two pixels reads as a mistake when they share a row, so the button is
// pinned to the control's height. Wrapping and overriding `button` is how CopyButton adjusts the
// shared button too; the alternative is a height prop on FlatButton that every other caller would
// have to ignore.
const FetchButtonWrap = styled.div`
  button {
    box-sizing: border-box;
    height: 38px;
    padding: 0 24px;
  }
`;

// The two actions under a record. Same 12px gap the specs list uses between its own mode buttons.
const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const ResultHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

export const FetchSpecForm = ({ initialId }) => {
  const navigate = useNavigate();
  const target = writeTarget();
  // The feature's own list hook, unfiltered — reused rather than fetching the list a second way.
  // The hook's status filter is SERVER-side, so the switch narrows the query rather than the array.
  const { visible, isLoading, error: listError, filters, setFilters, reload } = useScoreSpecs();

  const [id, setId] = React.useState(initialId || '');
  const [isFetching, setIsFetching] = React.useState(false);
  // What is in flight, which is not always what the picker holds: on a deep link the picker may still
  // be loading, and a filter change can drop the picked id out from under an in-flight fetch.
  const [pendingId, setPendingId] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [record, setRecord] = React.useState(null);

  const canFetch = id !== '' && !isFetching;

  // Takes the id rather than reading state, so the deep link can fetch before a render has passed.
  const fetchSpec = React.useCallback(async (specId) => {
    setIsFetching(true);
    setPendingId(specId);
    setError(null);
    const res = await getSpec(specId);
    setIsFetching(false);
    if (res.spec) {
      setRecord(res.spec);
      return;
    }
    // The previous result goes with the failed fetch: leaving it on screen under a new id would be
    // showing one spec while naming another.
    setRecord(null);
    setError(res.error);
  }, []);

  // `disabled` on the shared buttons is styling only — the click still fires — so the guard is here.
  const handleFetch = () => {
    if (!canFetch) return;
    fetchSpec(id);
  };

  // The deep link, once. Deliberately independent of the list: the record is fetched by id, so a slow
  // or failed list must not hold up (or cancel) the thing the URL asked for.
  const autoFetched = React.useRef(false);
  React.useEffect(() => {
    if (autoFetched.current || !initialId) return;
    autoFetched.current = true;
    fetchSpec(initialId);
  }, [initialId, fetchSpec]);

  const json = record ? JSON.stringify(record, null, 2) : '';

  const options = visible.map((spec) => ({
    value: spec.score_spec_id,
    label: `${spec.score_spec_id} — ${formatStatus(spec.status)}`,
  }));

  // `locked` is deliberately absent from the switch — four positions, and a locked spec is still
  // reachable under All. Add it here if that turns out to be the one people look for.
  const segments = statusSegments(['all', 'draft', 'approved', 'retired']);

  // A filter change can take the picked spec out of the list. The id is dropped so Fetch cannot fire
  // on something the dropdown is no longer showing; the record already fetched stays, because it is a
  // real answer that the header names.
  //
  // Not while the list is still in flight: `visible` is empty before it lands, and a deep-linked id
  // would be cleared by a list that had not yet had a chance to contain it.
  React.useEffect(() => {
    if (isLoading) return;
    if (id && !visible.some((spec) => spec.score_spec_id === id)) setId('');
  }, [visible, id, isLoading]);

  const countHint =
    filters.status === 'all'
      ? `${options.length} spec${options.length === 1 ? '' : 's'} in this store — every status, newest first.`
      : `${options.length} ${formatStatus(filters.status).toLowerCase()} spec${
          options.length === 1 ? '' : 's'
        }, newest first.`;

  return (
    <div>
      <EnvBar $production={target.isProduction}>
        <Body7 bold>{`Reading from ${target.label}`}</Body7>
        <Body7 isLowContrast>{target.host}</Body7>
        <Body7 isLowContrast>
          · Dev/QA and production are separate stores; an id in one may not exist in the other.
        </Body7>
      </EnvBar>

      <Container top={24}>
        {isLoading ? (
          <Body5 isLowContrast>Loading score specs…</Body5>
        ) : listError ? (
          <div>
            <Body5>{listError}</Body5>
            <Container top={16}>
              <SkeletonButton onClick={reload}>Retry</SkeletonButton>
            </Container>
          </div>
        ) : (
          <div>
            <SegmentedControl
              label="Status"
              options={segments}
              active={filters.status}
              onSelect={(status) => setFilters({ status })}
              withDots
              segmentWidth={110}
            />

            <Container top={16}>
              {options.length === 0 ? (
                <Body5 isLowContrast>
                  {filters.status === 'all'
                    ? 'This store holds no score specs yet.'
                    : `No ${formatStatus(filters.status).toLowerCase()} specs in this store.`}
                </Body5>
              ) : (
                <PickerRow>
                  <FieldWrap>
                    {/* The count rides in the control's own hint rather than on a line of its own: three
                  stacked low-contrast captions under one control is mush, and this is the slot for it. */}
                    <Select
                      id="fetch-spec-id"
                      label="Score spec id"
                      aria-label="Score spec id"
                      placeholder="Pick a spec…"
                      value={id}
                      onChange={setId}
                      options={options}
                      hint={countHint}
                    />
                  </FieldWrap>
                  <Container top={22}>
                    <FetchButtonWrap>
                      <FlatButton onClick={handleFetch} disabled={!canFetch}>
                        {isFetching ? 'Fetching…' : 'Fetch'}
                      </FlatButton>
                    </FetchButtonWrap>
                  </Container>
                </PickerRow>
              )}
            </Container>
          </div>
        )}
      </Container>

      {error && (
        <Container top={16}>
          <Consequence $tone="bad">{error}</Consequence>
        </Container>
      )}

      {record && (
        <Container top={24}>
          <ResultHeader>
            <MicroLabel>{`GET /score_specs/${record.score_spec_id}`}</MicroLabel>
            <CopyButton text={json} transparent />
          </ResultHeader>
          <Container top={8}>
            <ReadOnlyJson>{json}</ReadOnlyJson>
          </Container>
          <Container top={16}>
            <ActionsRow>
              <SkeletonButton onClick={() => navigate(`${ROUTES.scoreSpecSpecs}/${record.score_spec_id}`)}>
                Open in the advanced editor
              </SkeletonButton>
              {/* The edit mode used to be a button on the specs list, next to the one that opens this
                  screen. It sits here instead: reading the record is how you decide it needs
                  changing, and this is where that decision gets made.
                  
                  The mode copies APPROVED specs only, so the button says two different things. On an
                  approved record it is about this spec and deep-links it preselected; on anything
                  else the record cannot be a source at all, so it stays a jump to the mode and its
                  own picker, and the line underneath says which case this is. */}
              {record.status === 'approved' ? (
                <SkeletonButton onClick={() => navigate(`${ROUTES.editScoreSpec}/${record.score_spec_id}`)}>
                  Edit this spec
                </SkeletonButton>
              ) : (
                <SkeletonButton onClick={() => navigate(ROUTES.editScoreSpec)}>Edit an approved spec</SkeletonButton>
              )}
            </ActionsRow>
            <Container top={8}>
              <Body7 isLowContrast>
                {record.status === 'approved'
                  ? 'An approved spec is frozen, so editing it authors its successor — the form opens with this one already picked.'
                  : 'The edit mode copies approved specs only, so this one cannot be its source — the advanced editor is where it changes.'}
              </Body7>
            </Container>
          </Container>
        </Container>
      )}

      {/* A deep link arrives fetching, and on that path the picker above may still be loading — so
          the in-flight state has to be said here, naming the id, or the screen looks empty. */}
      {isFetching && !record && (
        <Container top={24}>
          <Body5 isLowContrast>{`Fetching ${pendingId}…`}</Body5>
        </Container>
      )}

      {/* The output area's empty state, and only that: it used to render while the list was still
          loading, while the list had failed, and on an empty store — three places where it was a
          stray sentence rather than a description of what goes here. */}
      {!record && !error && !isFetching && !isLoading && !listError && options.length > 0 && (
        <Container top={24}>
          <Body5 isLowContrast>
            The whole record comes back — the definition plus the operational columns around it.
          </Body5>
        </Container>
      )}
    </div>
  );
};
